import { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { NewsletterService } from '@ts/services/newsletterService';
import { useDataComponent } from '../preact';
import { NewsletterForm } from './NewsletterForm';
import {
  getNewsletterSignUpContent,
  getNewsLetterServiceProps,
} from './content';

export type Content = {
  title: string | JSX.Element;
  text?: string | JSX.Element;
  kicker?: string;
  image?: string;
  showImage?: boolean;
  wrapperClasses?: string;
  svg?: JSX.Element;
};

export type ContentType =
  | 'default'
  | 'breaker'
  | 'breakerFooter'
  | 'breakerRelated'
  | 'landingpage'
  | 'landingpageCompact'
  | 'landingpageBlueNewDeal'
  | 'blueNewDeal'
  | 'blueNewDealRelated'
  | 'newsletterpage';

export type Props = {
  type: ContentType;
  imageSrc: string;
  newsletterService?: NewsletterService;
  newsletterTitle?: string;
  newsletterText?: string;
  newsletterBtnText?: string;
};

export type Result = 'ok' | 'error';

const success = {
  title: 'Fast geschafft',
  text: 'Sie wissen vermutlich schon, was jetzt kommt: Eine E-Mail mit Bestätigungs-Link. Mit dem Klick auf den Link haben Sie wirklich alles geschafft.',
  kicker: 'Newsletter',
};

/**
 * Shows Newsletter according to assigned newsletter type.
 * @param props
 * @param props.type - newsletter type, responsible to identify the correct newsletter content and CSS individual specifications
 * @param props.imageSrc - image source string that defaults to an empty string
 * @param props.newsletterService - service instance that handles API logic
 * @param props.newsletterTitle - optional param to create stories
 * @param props.newsletterText - optional param to create stories
 * @param props.newsletterBtnText - optional param to create stories
 */
export const NewsletterSignUp = ({
  type,
  imageSrc,
  newsletterService = new NewsletterService('', ''),
  newsletterTitle,
  newsletterText,
  newsletterBtnText,
}: Props) => {
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [signUpError, setSignUpError] = useState(false);

  const handleSignUpMessage = (result: Result = 'ok') => {
    if (result === 'ok') {
      setSignUpSuccess(true);
    } else if (result === 'error') {
      setSignUpError(true);
    }
  };

  const content = getNewsletterSignUpContent({ type, imageSrc });
  const { title, text, kicker, image, showImage, wrapperClasses, svg } =
    content;

  return (
    <div className={wrapperClasses}>
      {svg}
      {image && (
        <figure
          className={`bc-newsletter__media ${(!showImage && 'u-display--none') || ''}`}
        >
          <div className="bc-newsletter__media__mask">
            <img
              className="bc-newsletter__media__img"
              src={image}
              loading="lazy"
              alt=""
            />
          </div>
        </figure>
      )}

      <div className="bc-newsletter__content">
        {signUpSuccess && (
          <>
            <h2 className="bc-newsletter__title">
              <span className="bc-newsletter__kicker">{success.kicker}</span>
              <span className="u-a11y-hidden">: </span>

              <div>{success.title}</div>
            </h2>

            <p className="bc-newsletter__text">{success.text}</p>
          </>
        )}

        {!signUpSuccess && (
          <>
            <h2 className="bc-newsletter__title">
              {kicker && (
                <>
                  <span className="bc-newsletter__kicker">{kicker}</span>
                  <span className="u-a11y-hidden">: </span>
                </>
              )}
              <div>{newsletterTitle || title}</div>
            </h2>

            {text && (
              <p className="bc-newsletter__text">{newsletterText || text}</p>
            )}

            <NewsletterForm
              handleSignUpMessage={handleSignUpMessage}
              signUpError={signUpError}
              newsletterService={newsletterService}
              newsletterBtnName={newsletterBtnText}
            />
          </>
        )}
      </div>
    </div>
  );
};

const contentTypeValidation = (type: unknown): ContentType | undefined => {
  switch (type) {
    case 'default':
    case 'breaker':
    case 'breakerFooter':
    case 'breakerRelated':
    case 'landingpage':
    case 'landingpageCompact':
    case 'landingpageBlueNewDeal':
    case 'blueNewDeal':
    case 'blueNewDealRelated':
    case 'newsletterpage':
      return type;

    default:
      return undefined;
  }
};

useDataComponent('.pts-newsletter-sign-up', dataset => {
  let validatedType = contentTypeValidation(dataset.type);
  if (!validatedType) {
    console.warn(
      'Newsletter SignUp Type was undefined: default value implemented',
    );

    validatedType = 'default';
  }

  const { url, tag } = getNewsLetterServiceProps(validatedType);

  return (
    <NewsletterSignUp
      type={validatedType}
      imageSrc={
        dataset.imageSrc ||
        '/++theme++freitag.theme24/freitag/newsletters/Mockup-iPhone-Front-Full.png'
      }
      newsletterService={new NewsletterService(url, tag)}
    />
  );
});
